import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { gql } from '../lib/api';
import {
  LATEST_TXN_DATES_QUERY,
  MONTHLY_SUMMARY_QUERY,
  STORES_DETAIL_QUERY,
  TRANSACTIONS_QUERY,
} from '../graphql/queries';
import { ProductBarChart } from '../components/charts/ProductBarChart';
import { PaymentDonut } from '../components/charts/PaymentDonut';
import { WdayHourGrid } from '../components/charts/WdayHourGrid';
import { CategoryHourGrid } from '../components/charts/CategoryHourGrid';
import { useSelectedStore } from '../hooks/useSelectedStore';
import { airRegiBusinessDate, type AirRegiTransactionTime } from '../lib/airRegiBusinessTime';
import {
  currentYearMonth,
  extractYearMonthFromPeriodKey,
  recentYearMonthRange,
} from '../lib/yearMonth';
import {
  categoryHourMatrixByGroup,
  countOperatingDays,
  dailySeries,
  filterByBusinessMonth,
  filterByLD,
  hourlyFoodDrink,
  hourlySpend,
  isJournalTxn,
  ldKpis,
  partySizeBins,
  productAbc,
  productAbcByGroup,
  seatStats,
  spendDistribution,
  staffStats,
  stayDurationDist,
  wdayHourMatrix,
  type StoreLD,
  type TxnLite,
} from '../lib/salesAnalytics';

interface StoreDetail extends StoreLD {
  name: string;
}

type Transaction = TxnLite;

type LDFilter = 'all' | 'lunch' | 'dinner';

// ============================================================================
// 期間ヘルパ
// ============================================================================
const monthRange = (ym: string): { from: string; to: string } => {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    from: `${ym}-01`,
    to: `${ym}-${String(last).padStart(2, '0')}`,
  };
};
const nextDay = (date: string): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};
const prevMonth = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const prevYearSameMonth = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  return `${y - 1}-${String(m).padStart(2, '0')}`;
};

const fmtYen = (n: number | null | undefined): string =>
  typeof n === 'number' && Number.isFinite(n) ? `¥${Math.round(n).toLocaleString()}` : '—';
const fmtInt = (n: number | null | undefined): string =>
  typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—';
const fmtRatio = (n: number | null | undefined, digits = 1): string =>
  typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(digits)}%` : '—';

// 前期比（A vs B）の計算
const yoyDiff = (cur: number, prev: number): { diff: number; pct: number | null } => {
  const diff = cur - prev;
  const pct = prev > 0 ? (diff / prev) * 100 : null;
  return { diff, pct };
};

// ============================================================================
export const SalesPage = ({ isAdmin }: { isAdmin: boolean }) => {
  // URL クエリパラメータと state を双方向同期
  // 共有 URL で同じ画面を再現できる
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedStoreId, setStoredStoreId] = useSelectedStore();
  const storeParam = searchParams.get('store');
  const ymParam = searchParams.get('ym');
  const ldParam = searchParams.get('ld');
  const initLd: LDFilter =
    ldParam === 'all' || ldParam === 'lunch' || ldParam === 'dinner' ? ldParam : 'all';
  const initYearMonth = /^\d{4}-\d{2}$/.test(ymParam ?? '') ? (ymParam as string) : '';

  // store: URL > localStorage（useSelectedStore）> 自動選択
  const [storeId, setStoreId] = useState<string>(storeParam || storedStoreId);
  const [yearMonth, setYearMonth] = useState(initYearMonth);
  const [monthInitialized, setMonthInitialized] = useState(!!initYearMonth);
  const [ld, setLd] = useState<LDFilter>(initLd);

  // store 変更時に localStorage も追従（他ページとの整合性）
  useEffect(() => {
    if (storeId) setStoredStoreId(storeId);
  }, [storeId, setStoredStoreId]);

  const storesQ = useQuery({
    queryKey: ['storesDetail'],
    queryFn: () => gql<{ stores: StoreDetail[] }>(STORES_DETAIL_QUERY),
  });
  const stores = useMemo(() => storesQ.data?.stores ?? [], [storesQ.data?.stores]);
  const effectiveStoreId = stores.find((s) => s.id === storeId)?.id ?? stores[0]?.id ?? '';

  // state 変更を URL に反映（デフォルト値や空の値は省略）
  useEffect(() => {
    const next = new URLSearchParams();
    if (effectiveStoreId) next.set('store', effectiveStoreId);
    if (yearMonth) next.set('ym', yearMonth);
    if (ld !== 'all') next.set('ld', ld);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [effectiveStoreId, yearMonth, ld, searchParams, setSearchParams]);

  // 最新月を初期値に
  const { fromYearMonth: probeFrom, toYearMonth: probeTo } =
    useMemo(() => recentYearMonthRange(12), []);
  const recentMonthlyQ = useQuery({
    enabled: !!effectiveStoreId,
    queryKey: ['monthlyForSalesProbe', effectiveStoreId, probeFrom, probeTo],
    queryFn: () =>
      gql<{ monthlySummary: { periodKey: string }[] }>(MONTHLY_SUMMARY_QUERY, {
        storeId: effectiveStoreId,
        fromYearMonth: probeFrom,
        toYearMonth: probeTo,
      }),
  });
  const probeCurrentMonth = useMemo(() => currentYearMonth(), []);
  const latestProbeFrom = monthRange(prevMonth(probeCurrentMonth)).from;
  const latestProbeTo = nextDay(monthRange(probeCurrentMonth).to);
  const latestDatesQ = useQuery({
    enabled: !!effectiveStoreId,
    queryKey: ['latestSalesBusinessMonth', effectiveStoreId, latestProbeFrom, latestProbeTo],
    queryFn: () => gql<{ transactions: AirRegiTransactionTime[] }>(LATEST_TXN_DATES_QUERY, {
      storeId: effectiveStoreId, from: latestProbeFrom, to: latestProbeTo,
    }),
  });
  const dayChangeTime = stores.find((store) => store.id === effectiveStoreId)?.airregiBusinessDayStart;
  const latestBusinessMonth = latestDatesQ.data?.transactions
    ?.map((txn) => airRegiBusinessDate(txn, dayChangeTime).slice(0, 7))
    .sort()
    .at(-1);
  const latestAvailableMonth = latestBusinessMonth ??
    recentMonthlyQ.data?.monthlySummary
      ?.map((s) => extractYearMonthFromPeriodKey(s.periodKey))
      .sort()
      .at(-1) ?? currentYearMonth();
  useEffect(() => {
    setMonthInitialized(false);
    setYearMonth('');
  }, [effectiveStoreId]);
  useEffect(() => {
    if (!effectiveStoreId || monthInitialized || recentMonthlyQ.isLoading || latestDatesQ.isLoading) return;
    setYearMonth(latestAvailableMonth);
    setMonthInitialized(true);
  }, [effectiveStoreId, latestAvailableMonth, monthInitialized, recentMonthlyQ.isLoading, latestDatesQ.isLoading]);

  const { from, to } = yearMonth ? monthRange(yearMonth) : { from: '', to: '' };

  // TXN のキーは会計日。月末の翌日未明に会計した分も取得し、営業日で月内に絞る。
  const txnsCurrentQ = useQuery({
    enabled: !!effectiveStoreId && !!yearMonth && monthInitialized,
    queryKey: ['txns', effectiveStoreId, from, to],
    queryFn: () =>
      gql<{ transactions: Transaction[] }>(TRANSACTIONS_QUERY, {
        storeId: effectiveStoreId,
        from,
        to: nextDay(to),
      }),
  });

  const prevYm = useMemo(() => (yearMonth ? prevMonth(yearMonth) : ''), [yearMonth]);
  const prevR = useMemo(() => (prevYm ? monthRange(prevYm) : null), [prevYm]);
  const txnsPrevMonthQ = useQuery({
    enabled: !!effectiveStoreId && !!prevR && monthInitialized,
    queryKey: ['txns-prev', effectiveStoreId, prevR?.from ?? '', prevR?.to ?? ''],
    queryFn: () =>
      gql<{ transactions: Transaction[] }>(TRANSACTIONS_QUERY, {
        storeId: effectiveStoreId,
        from: prevR!.from,
        to: nextDay(prevR!.to),
      }),
  });

  const prevYearYm = useMemo(() => (yearMonth ? prevYearSameMonth(yearMonth) : ''), [yearMonth]);
  const prevYearR = useMemo(
    () => (prevYearYm ? monthRange(prevYearYm) : null),
    [prevYearYm],
  );
  const txnsPrevYearQ = useQuery({
    enabled: !!effectiveStoreId && !!prevYearR && monthInitialized,
    queryKey: ['txns-prevyear', effectiveStoreId, prevYearR?.from ?? '', prevYearR?.to ?? ''],
    queryFn: () =>
      gql<{ transactions: Transaction[] }>(TRANSACTIONS_QUERY, {
        storeId: effectiveStoreId,
        from: prevYearR!.from,
        to: nextDay(prevYearR!.to),
      }),
  });

  // ========================================================================
  // 集計（L/D フィルタ反映）
  // ========================================================================
  const curStores = useMemo(
    () => stores.filter((s) => s.id === effectiveStoreId),
    [stores, effectiveStoreId],
  );
  const allCurTxns = useMemo(
    () => filterByBusinessMonth(txnsCurrentQ.data?.transactions ?? [], curStores, yearMonth),
    [txnsCurrentQ.data, curStores, yearMonth],
  );
  const allPrevMonthTxns = useMemo(
    () => filterByBusinessMonth(txnsPrevMonthQ.data?.transactions ?? [], curStores, prevYm),
    [txnsPrevMonthQ.data, curStores, prevYm],
  );
  const allPrevYearTxns = useMemo(
    () => filterByBusinessMonth(txnsPrevYearQ.data?.transactions ?? [], curStores, prevYearYm),
    [txnsPrevYearQ.data, curStores, prevYearYm],
  );
  const filtered = useMemo(
    () => filterByLD(allCurTxns, curStores, ld),
    [allCurTxns, curStores, ld],
  );
  const filteredPrev = useMemo(
    () => filterByLD(allPrevMonthTxns, curStores, ld),
    [allPrevMonthTxns, curStores, ld],
  );
  const filteredPrevYear = useMemo(
    () => filterByLD(allPrevYearTxns, curStores, ld),
    [allPrevYearTxns, curStores, ld],
  );

  // 主要 KPI（合算）
  const kpi = useMemo(() => {
    const sales = filtered.reduce((s, t) => s + t.total, 0);
    const cust = filtered.reduce((s, t) => s + (t.customerCount ?? 0), 0);
    const txnCount = filtered.length;
    const stayValid = filtered.filter((t) => (t.stayDurationSec ?? 0) > 0);
    const avgStay = stayValid.length
      ? stayValid.reduce((s, t) => s + (t.stayDurationSec ?? 0), 0) / stayValid.length
      : 0;
    return {
      sales,
      customerCount: cust,
      txnCount,
      avgSpend: cust > 0 ? sales / cust : null,
      avgPartySize: txnCount > 0 ? cust / txnCount : null,
      avgStayMin: avgStay / 60,
    };
  }, [filtered]);

  const kpiPrev = useMemo(() => {
    const sales = filteredPrev.reduce((s, t) => s + t.total, 0);
    const cust = filteredPrev.reduce((s, t) => s + (t.customerCount ?? 0), 0);
    return { sales, customerCount: cust, avgSpend: cust > 0 ? sales / cust : null };
  }, [filteredPrev]);

  const kpiPrevYear = useMemo(() => {
    const sales = filteredPrevYear.reduce((s, t) => s + t.total, 0);
    const cust = filteredPrevYear.reduce((s, t) => s + (t.customerCount ?? 0), 0);
    return { sales, customerCount: cust, avgSpend: cust > 0 ? sales / cust : null };
  }, [filteredPrevYear]);

  // 各分析
  const spendBins = useMemo(() => spendDistribution(filtered), [filtered]);
  const hourSpendData = useMemo(() => hourlySpend(filtered, curStores), [filtered, curStores]);
  const partyData = useMemo(() => partySizeBins(filtered), [filtered]);
  const wdayHour = useMemo(() => wdayHourMatrix(filtered, curStores), [filtered, curStores]);
  const dailyCur = useMemo(() => dailySeries(filtered, curStores), [filtered, curStores]);
  const dailyPrev = useMemo(() => dailySeries(filteredPrev, curStores), [filteredPrev, curStores]);
  const dailyPrevYear = useMemo(() => dailySeries(filteredPrevYear, curStores), [filteredPrevYear, curStores]);
  const stayBins = useMemo(() => stayDurationDist(filtered), [filtered]);
  const fdHourly = useMemo(() => hourlyFoodDrink(filtered, undefined, curStores), [filtered, curStores]);
  const catHourGroups = useMemo(() => categoryHourMatrixByGroup(filtered, undefined, 8, curStores), [filtered, curStores]);
  const staffData = useMemo(() => staffStats(filtered), [filtered]);
  const operatingDays = useMemo(() => countOperatingDays(filtered, curStores), [filtered, curStores]);
  const seatData = useMemo(() => seatStats(filtered, operatingDays), [filtered, operatingDays]);
  const productRows = useMemo(() => productAbc(filtered), [filtered]);
  const productGroups = useMemo(() => productAbcByGroup(filtered), [filtered]);
  const ldRows = useMemo(() => ldKpis(allCurTxns, curStores), [allCurTxns, curStores]);

  // ジャーナル履歴由来の取引が混じっているか判定
  //   ジャーナル取引は人数 / 滞在時間 / 席名 / カテゴリが欠損するため、関連 KPI に注釈を出す
  const journalCount = useMemo(() => filtered.filter(isJournalTxn).length, [filtered]);
  const hasJournalMix = journalCount > 0;
  const allJournal = filtered.length > 0 && journalCount === filtered.length;

  // 日別推移用：日付ベースの統合（同じ「日」を current/prev/prevYear で並べる）
  const dailyMerged = useMemo(() => {
    // 当月の日付配列（YYYY-MM-DD）を基準に、前月・前年同月は「日」だけ揃える
    const days = dailyCur.map((d) => d);
    const prevByDay = new Map<number, (typeof dailyPrev)[number]>();
    for (const p of dailyPrev) {
      prevByDay.set(Number(p.date.slice(8, 10)), p);
    }
    const prevYearByDay = new Map<number, (typeof dailyPrevYear)[number]>();
    for (const p of dailyPrevYear) {
      prevYearByDay.set(Number(p.date.slice(8, 10)), p);
    }
    return days.map((d) => {
      const day = Number(d.date.slice(8, 10));
      return {
        label: `${day}日`,
        sales: d.sales,
        customers: d.customerCount,
        prevSales: prevByDay.get(day)?.sales ?? 0,
        prevYearSales: prevYearByDay.get(day)?.sales ?? 0,
      };
    });
  }, [dailyCur, dailyPrev, dailyPrevYear]);

  const isLoading =
    storesQ.isLoading ||
    txnsCurrentQ.isLoading ||
    txnsPrevMonthQ.isLoading ||
    txnsPrevYearQ.isLoading ||
    recentMonthlyQ.isLoading ||
    latestDatesQ.isLoading;

  const showLunchToggle = stores.find((s) => s.id === effectiveStoreId)?.lunchStart != null;
  const paymentByMethod = useMemo(() => {
    const amounts = new Map<string, number>();
    for (const txn of allCurTxns) {
      for (const payment of txn.byPayment ?? []) {
        amounts.set(payment.method, (amounts.get(payment.method) ?? 0) + payment.amount);
      }
    }
    return Array.from(amounts, ([method, amount]) => ({ method, amount }));
  }, [allCurTxns]);

  // ABC 累計構成比（フード / ドリンク 別）
  const totalSalesProducts = productRows.reduce((s, p) => s + p.sales, 0);
  const totalFoodSales = productGroups.food.reduce((s, p) => s + p.sales, 0);
  const totalDrinkSales = productGroups.drink.reduce((s, p) => s + p.sales, 0);
  void totalSalesProducts;

  return (
    <div>
      <div style={pageHeader}>
        <h1 style={pageTitle}>売上分析</h1>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <select
            value={effectiveStoreId}
            onChange={(e) => setStoreId(e.target.value)}
            style={selectStyle}
          >
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <input
            type="month"
            value={yearMonth}
            onChange={(e) => {
              setYearMonth(e.target.value);
              setMonthInitialized(true);
            }}
            style={selectStyle}
          />
          <div style={chipGroup}>
            <button
              type="button"
              onClick={() => setLd('all')}
              style={ld === 'all' ? chipActive : chip}
            >
              全時間帯
            </button>
            {showLunchToggle && (
              <button
                type="button"
                onClick={() => setLd('lunch')}
                style={ld === 'lunch' ? chipActive : chip}
              >
                ランチ
              </button>
            )}
            <button
              type="button"
              onClick={() => setLd('dinner')}
              style={ld === 'dinner' ? chipActive : chip}
            >
              ディナー
            </button>
          </div>
        </div>
      </div>

      {/* ジャーナル履歴由来取引の混在警告 */}
      {hasJournalMix && (
        <section style={journalWarning}>
          <strong>{allJournal ? '⚠ 過去分（ジャーナル履歴）データのみ表示' : `⚠ ジャーナル履歴由来の取引が ${journalCount}/${filtered.length} 件混在`}</strong>
          <div style={{ fontSize: '0.78rem', color: '#7a4a00', marginTop: 4 }}>
            ジャーナル履歴は過去分の補完データで、<strong>人数・滞在時間・席名・カテゴリ名が取得できません</strong>。
            このため <em>客単価・客数・パーティサイズ別・滞在時間分布・席別売上・カテゴリ × 時間帯</em>
            の各分析は欠損または不正確になります（売上・取引数・商品 ABC・時間帯売上・決済方法・F/D 比率は影響なし）。
          </div>
        </section>
      )}

      {/* サマリー KPI（前月比・前年同月比） */}
      <section style={kpiGrid}>
        <CompareKpi
          label="売上"
          value={fmtYen(kpi.sales)}
          prev={kpiPrev.sales}
          prevYear={kpiPrevYear.sales}
          cur={kpi.sales}
          fmt="yen"
        />
        <CompareKpi
          label="客数"
          value={fmtInt(kpi.customerCount)}
          prev={kpiPrev.customerCount}
          prevYear={kpiPrevYear.customerCount}
          cur={kpi.customerCount}
          fmt="int"
        />
        <CompareKpi
          label="客単価"
          value={fmtYen(kpi.avgSpend)}
          prev={kpiPrev.avgSpend ?? 0}
          prevYear={kpiPrevYear.avgSpend ?? 0}
          cur={kpi.avgSpend ?? 0}
          fmt="yen"
        />
        <KpiSimple label="組数（取引数）" value={fmtInt(kpi.txnCount)} sub={`平均 ${kpi.avgPartySize?.toFixed(1) ?? '—'} 人/組`} />
        <KpiSimple label="平均滞在時間" value={`${kpi.avgStayMin.toFixed(0)} 分`} sub={`営業日数 ${operatingDays} 日`} />
      </section>

      {isLoading && (
        <p style={muted}>読込中…</p>
      )}

      {/* ============================================================
          ランチ vs ディナー比較
         ============================================================ */}
      <section style={cardSection}>
        <h2 style={sectionTitle}>ランチ／ディナー 比較（{yearMonth || '—'}）</h2>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>区分</th>
              <th style={{ ...th, textAlign: 'right' }}>売上</th>
              <th style={{ ...th, textAlign: 'right' }}>取引数</th>
              <th style={{ ...th, textAlign: 'right' }}>客数</th>
              <th style={{ ...th, textAlign: 'right' }}>客単価</th>
              <th style={{ ...th, textAlign: 'right' }}>組当り人数</th>
              <th style={{ ...th, textAlign: 'right' }}>売上構成比</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const total = ldRows.reduce((s, r) => s + r.sales, 0);
              return ldRows.map((r) => (
                <tr key={r.segment}>
                  <td style={td}>
                    <span style={ldBadge(r.segment)}>{r.segment}</span>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtYen(r.sales)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtInt(r.txnCount)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtInt(r.customerCount)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtYen(r.avgSpend)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {r.avgPartySize != null ? r.avgPartySize.toFixed(1) : '—'}
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: '#868e96' }}>
                    {total > 0 ? `${((r.sales / total) * 100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </section>

      {/* ============================================================
          A. 客単価の深掘り
         ============================================================ */}
      <h2 style={blockTitle}>A. 客単価分析</h2>

      <section style={cardSection}>
        <h3 style={sectionTitle}>客単価分布（500円刻み）</h3>
        {spendBins.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={spendBins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="range" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                formatter={(v) => [`${Number(v).toLocaleString()} 件`, '取引数']}
                labelFormatter={(l) => `客単価 ${l} 〜`}
              />
              <Bar dataKey="count" name="取引数" fill="#0088b0" />
              {kpi.avgSpend != null && (
                <ReferenceLine
                  x={`¥${(Math.floor(kpi.avgSpend / 500) * 500).toLocaleString()}`}
                  stroke="#aa0b56"
                  strokeDasharray="4 4"
                  label={{ value: `平均 ${fmtYen(kpi.avgSpend)}`, fontSize: 10, fill: '#aa0b56', position: 'top' }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
        <p style={hint}>横軸＝客単価帯（売上÷客数を 500 円刻みでバケット化）、縦軸＝該当する取引（伝票）数。赤線＝当月の平均客単価</p>
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>時間帯別 客単価 / 取引数</h3>
        {hourSpendData.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={hourSpendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}時`} />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                label={{ value: '取引数', angle: -90, position: 'insideLeft', fontSize: 11 }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(v) => `¥${Math.round(Number(v) / 1000)}k`}
                tick={{ fontSize: 11 }}
                label={{ value: '客単価', angle: 90, position: 'insideRight', fontSize: 11 }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === '客単価') return [fmtYen(Number(value)), name];
                  return [Number(value).toLocaleString(), name];
                }}
                labelFormatter={(l) => `${l} 時台`}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="txnCount" name="取引数" fill="#adb5bd" />
              <Line yAxisId="right" type="monotone" dataKey="avgSpend" name="客単価" stroke="#aa0b56" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>パーティサイズ別 客単価</h3>
        {partyData.every((p) => p.txnCount === 0) ? (
          <p style={muted}>データなし</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>組構成</th>
                <th style={{ ...th, textAlign: 'right' }}>取引数</th>
                <th style={{ ...th, textAlign: 'right' }}>客数</th>
                <th style={{ ...th, textAlign: 'right' }}>売上</th>
                <th style={{ ...th, textAlign: 'right' }}>客単価</th>
                <th style={{ ...th, textAlign: 'right' }}>組単価</th>
                <th style={{ ...th, textAlign: 'right' }}>取引構成比</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const totalCnt = partyData.reduce((s, r) => s + r.txnCount, 0);
                return partyData.map((r) => (
                  <tr key={r.bucket}>
                    <td style={td}>{r.bucket}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtInt(r.txnCount)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtInt(r.customerCount)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtYen(r.sales)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtYen(r.avgSpend)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtYen(r.avgPartySpend)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#868e96' }}>
                      {totalCnt > 0 ? `${((r.txnCount / totalCnt) * 100).toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        )}
      </section>

      {/* ============================================================
          B. 来客動態
         ============================================================ */}
      <h2 style={blockTitle}>B. 来客動態</h2>

      <section style={cardSection}>
        <h3 style={sectionTitle}>曜日 × 時間帯 ヒートマップ（取引数）</h3>
        {wdayHour.cells.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <WdayHourGrid
            cells={wdayHour.cells}
            minHour={wdayHour.minHour}
            maxHour={wdayHour.maxHour}
            metric="txnCount"
          />
        )}
        <p style={hint}>セルの数値＝取引数、色濃度＝取引数の相対値。月〜日 × 営業時間帯。</p>
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>日別 売上推移（前月・前年同月比）</h3>
        {dailyMerged.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={dailyMerged}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                yAxisId="left"
                tickFormatter={(v) => `${Math.round(Number(v) / 10000)}万`}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(v, n) => {
                  if (n === '来客数') return [Number(v).toLocaleString(), n];
                  return [fmtYen(Number(v)), n];
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="sales" name="当月 売上" fill="#aa0b56" />
              <Line yAxisId="left" type="monotone" dataKey="prevSales" name={`前月 ${prevYm}`} stroke="#0088b0" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
              <Line yAxisId="left" type="monotone" dataKey="prevYearSales" name={`前年同月 ${prevYearYm}`} stroke="#6b6379" strokeWidth={1.5} strokeDasharray="2 4" dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="customers" name="来客数" stroke="#edbb00" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>滞在時間 分布</h3>
        {stayBins.every((b) => b.count === 0) ? (
          <p style={muted}>滞在時間データなし（CSVに非含有の場合あり）</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stayBins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v) => [`${Number(v).toLocaleString()} 件`, '取引数']} />
              <Bar dataKey="count" name="取引数" fill="#15aabf" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>

      {/* ============================================================
          C. メニューミックス
         ============================================================ */}
      <h2 style={blockTitle}>C. メニューミックス</h2>

      <section style={cardSection}>
        <h3 style={sectionTitle}>フード / ドリンク 比率（時間帯別）</h3>
        {fdHourly.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={fdHourly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}時`} />
              <YAxis
                yAxisId="left"
                tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(v) => `${v}%`}
                tick={{ fontSize: 11 }}
                domain={[0, 100]}
              />
              <Tooltip
                formatter={(v, n) => {
                  if (n === 'ドリンク比率') return [fmtRatio(Number(v)), n];
                  return [fmtYen(Number(v)), n];
                }}
                labelFormatter={(l) => `${l} 時台`}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="food" name="フード" stackId="fd" fill="#d6006c" />
              <Bar yAxisId="left" dataKey="drink" name="ドリンク" stackId="fd" fill="#edbb00" />
              <Line yAxisId="right" type="monotone" dataKey="drinkRatio" name="ドリンク比率" stroke="#212529" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <p style={hint}>店舗マスタの drinkCategoryKeywords に該当するカテゴリをドリンク扱い（未設定時は既定キーワードで判定）。</p>
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>フードカテゴリ × 時間帯 ヒートマップ（売上 上位 8 カテゴリ）</h3>
        {catHourGroups.food.cells.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <CategoryHourGrid
            cells={catHourGroups.food.cells}
            categories={catHourGroups.food.categories}
            hours={catHourGroups.food.hours}
          />
        )}
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>ドリンクカテゴリ × 時間帯 ヒートマップ（売上 上位 8 カテゴリ）</h3>
        {catHourGroups.drink.cells.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <CategoryHourGrid
            cells={catHourGroups.drink.cells}
            categories={catHourGroups.drink.categories}
            hours={catHourGroups.drink.hours}
          />
        )}
        <p style={hint}>
          ※ それぞれ独立したスケールで描画。ビール vs 豚玉のようにレンジが違うものを同一グリッドで比較しないため、純粋にフード／ドリンク別のピーク時間が読み取れる。
        </p>
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>
          フード商品 ABC（売上トップ 15）
          <span style={subTitleHint}>合計 {fmtYen(totalFoodSales)}</span>
        </h3>
        {productGroups.food.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <ProductBarChart data={productGroups.food} maxItems={15} />
        )}
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>フード商品明細（売上順、ABC ランク付）</h3>
        {productGroups.food.length === 0 ? (
          <p style={muted}>—</p>
        ) : (
          <AbcTable rows={productGroups.food} totalSales={totalFoodSales} />
        )}
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>
          ドリンク商品 ABC（売上トップ 15）
          <span style={subTitleHint}>合計 {fmtYen(totalDrinkSales)}</span>
        </h3>
        {productGroups.drink.length === 0 ? (
          <p style={muted}>データなし</p>
        ) : (
          <ProductBarChart data={productGroups.drink} maxItems={15} />
        )}
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>ドリンク商品明細（売上順、ABC ランク付）</h3>
        {productGroups.drink.length === 0 ? (
          <p style={muted}>—</p>
        ) : (
          <AbcTable rows={productGroups.drink} totalSales={totalDrinkSales} />
        )}
        <p style={hint}>
          ※ フード／ドリンクの判定は店舗マスタ「ドリンク判定キーワード」がカテゴリ名に含まれるかで決定（未設定時は既定キーワード）。
        </p>
      </section>

      {/* ============================================================
          D. オペレーション
         ============================================================ */}
      <h2 style={blockTitle}>D. オペレーション</h2>

      {isAdmin && <section style={cardSection}>
        <h3 style={sectionTitle}>スタッフ別 売上（接客スタッフ）</h3>
        {staffData.length === 0 ? (
          <p style={muted}>—</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>スタッフ</th>
                <th style={{ ...th, textAlign: 'right' }}>取引数</th>
                <th style={{ ...th, textAlign: 'right' }}>客数</th>
                <th style={{ ...th, textAlign: 'right' }}>売上</th>
                <th style={{ ...th, textAlign: 'right' }}>客単価</th>
              </tr>
            </thead>
            <tbody>
              {staffData.map((s) => (
                <tr key={s.staffName}>
                  <td style={td}>{s.staffName}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtInt(s.txnCount)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtInt(s.customerCount)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtYen(s.sales)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtYen(s.avgSpend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>}

      <section style={cardSection}>
        <h3 style={sectionTitle}>席別 売上 / 回転率（営業日数 {operatingDays} 日）</h3>
        {seatData.length === 0 ? (
          <p style={muted}>—</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>席</th>
                <th style={{ ...th, textAlign: 'right' }}>利用回数</th>
                <th style={{ ...th, textAlign: 'right' }}>売上</th>
                <th style={{ ...th, textAlign: 'right' }}>客数</th>
                <th style={{ ...th, textAlign: 'right' }}>1日あたり回転</th>
              </tr>
            </thead>
            <tbody>
              {seatData.slice(0, 30).map((s) => (
                <tr key={s.receiptName}>
                  <td style={td}>{s.receiptName}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtInt(s.txnCount)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtYen(s.sales)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtInt(s.customerCount)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {s.turnoverPerDay != null ? `${s.turnoverPerDay.toFixed(2)} 回/日` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={cardSection}>
        <h3 style={sectionTitle}>決済方法別 売上構成（{yearMonth || '—'}）</h3>
        {paymentByMethod.length === 0 ? (
          <p style={muted}>—</p>
        ) : (
          <PaymentDonut data={paymentByMethod} />
        )}
        <p style={hint}>
          ※ 決済方法は会計明細の取引単位の内訳から営業日で集計（時間帯フィルター適用外）
        </p>
      </section>
    </div>
  );
};

// ============================================================================
// 比較 KPI カード
// ============================================================================
interface CompareKpiProps {
  label: string;
  value: string;
  prev: number;
  prevYear: number;
  cur: number;
  fmt: 'yen' | 'int';
}
const CompareKpi = ({ label, value, prev, prevYear, cur, fmt }: CompareKpiProps) => {
  const m = yoyDiff(cur, prev);
  const y = yoyDiff(cur, prevYear);
  const fmtVal = (n: number): string => (fmt === 'yen' ? fmtYen(n) : fmtInt(n));
  return (
    <div style={kpiCardStyle}>
      <div style={kpiLabel}>{label}</div>
      <div style={kpiValue}>{value}</div>
      <div style={kpiSub}>
        <span style={diffStyle(m.pct)}>
          前月比 {m.pct == null ? '—' : `${m.pct >= 0 ? '+' : ''}${m.pct.toFixed(1)}%`}
        </span>
        <span style={{ marginLeft: 8, color: '#adb5bd' }}>{fmtVal(prev)}</span>
      </div>
      <div style={kpiSub}>
        <span style={diffStyle(y.pct)}>
          前年同月 {y.pct == null ? '—' : `${y.pct >= 0 ? '+' : ''}${y.pct.toFixed(1)}%`}
        </span>
        <span style={{ marginLeft: 8, color: '#adb5bd' }}>{fmtVal(prevYear)}</span>
      </div>
    </div>
  );
};

interface KpiSimpleProps {
  label: string;
  value: string;
  sub?: string;
}
const KpiSimple = ({ label, value, sub }: KpiSimpleProps) => (
  <div style={kpiCardStyle}>
    <div style={kpiLabel}>{label}</div>
    <div style={kpiValue}>{value}</div>
    {sub && <div style={kpiSub}>{sub}</div>}
  </div>
);

// ABC ランク付き商品明細テーブル
interface AbcTableProps {
  rows: { menuName: string; quantity: number; sales: number }[];
  totalSales: number;
  maxRows?: number;
}
const AbcTable = ({ rows, totalSales, maxRows = 50 }: AbcTableProps) => {
  const rankedRows = rows.slice(0, maxRows).reduce<
    Array<{ row: AbcTableProps['rows'][number]; ratio: number; cumulativeRatio: number }>
  >((result, row) => {
    const ratio = totalSales > 0 ? (row.sales / totalSales) * 100 : 0;
    const cumulativeRatio = (result.at(-1)?.cumulativeRatio ?? 0) + ratio;
    return [...result, { row, ratio, cumulativeRatio }];
  }, []);

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={th}>順位</th>
          <th style={th}>商品名</th>
          <th style={{ ...th, textAlign: 'right' }}>出数</th>
          <th style={{ ...th, textAlign: 'right' }}>売上</th>
          <th style={{ ...th, textAlign: 'right' }}>構成比</th>
          <th style={{ ...th, textAlign: 'right' }}>累計構成比</th>
        </tr>
      </thead>
      <tbody>
        {rankedRows.map(({ row: p, ratio, cumulativeRatio }, i) => {
          const tier = cumulativeRatio <= 70 ? 'A' : cumulativeRatio <= 90 ? 'B' : 'C';
          return (
            <tr key={p.menuName}>
              <td style={td}>
                <span style={tierBadge(tier)}>{tier}</span> {i + 1}
              </td>
              <td style={td}>{p.menuName}</td>
              <td style={{ ...td, textAlign: 'right' }}>{p.quantity}</td>
              <td style={{ ...td, textAlign: 'right' }}>{fmtYen(p.sales)}</td>
              <td style={{ ...td, textAlign: 'right' }}>{ratio.toFixed(1)}%</td>
              <td style={{ ...td, textAlign: 'right', color: '#868e96' }}>
                {cumulativeRatio.toFixed(1)}%
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const diffStyle = (pct: number | null): React.CSSProperties => ({
  color: pct == null ? '#868e96' : pct >= 0 ? '#006d8d' : '#aa0b56',
  fontSize: '0.78rem',
  fontWeight: 600,
});

const tierBadge = (tier: 'A' | 'B' | 'C'): React.CSSProperties => ({
  display: 'inline-block',
  width: 22,
  textAlign: 'center',
  padding: '0.05rem 0',
  borderRadius: 4,
  fontSize: '0.7rem',
  fontWeight: 700,
  marginRight: 4,
  background: tier === 'A' ? '#fff5f5' : tier === 'B' ? '#fff9db' : '#f1f3f5',
  color: tier === 'A' ? '#aa0b56' : tier === 'B' ? '#9c8005' : '#495057',
});

const ldBadge = (seg: 'ランチ' | 'ディナー' | 'その他'): React.CSSProperties => ({
  display: 'inline-block',
  padding: '0.1rem 0.45rem',
  borderRadius: 4,
  fontSize: '0.72rem',
  fontWeight: 600,
  background:
    seg === 'ランチ' ? '#fff4e6' : seg === 'ディナー' ? '#e7f5ff' : '#f1f3f5',
  color: seg === 'ランチ' ? '#d9480f' : seg === 'ディナー' ? '#0088b0' : '#495057',
});

// ============================================================================
// スタイル
// ============================================================================
const pageHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
  flexWrap: 'wrap',
  gap: '0.5rem',
};
const pageTitle: React.CSSProperties = { margin: 0, fontSize: '1.4rem' };
const selectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
};
const chipGroup: React.CSSProperties = { display: 'flex', gap: 4, alignItems: 'center' };
const chip: React.CSSProperties = {
  padding: '0.3rem 0.7rem',
  borderRadius: 999,
  border: '1px solid #dee2e6',
  background: '#fff',
  color: '#495057',
  cursor: 'pointer',
  fontSize: '0.82rem',
};
const chipActive: React.CSSProperties = {
  ...chip,
  background: '#aa0b56',
  borderColor: '#aa0b56',
  color: '#fff',
  fontWeight: 600,
};
const kpiGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1rem',
};
const kpiCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '0.9rem 1rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
};
const kpiLabel: React.CSSProperties = { color: '#6c757d', fontSize: '0.8rem' };
const kpiValue: React.CSSProperties = { fontSize: '1.4rem', fontWeight: 700, marginTop: 4 };
const kpiSub: React.CSSProperties = { fontSize: '0.78rem', color: '#868e96', marginTop: 4 };
const cardSection: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const subTitleHint: React.CSSProperties = {
  marginLeft: '0.6rem',
  fontSize: '0.78rem',
  color: '#868e96',
  fontWeight: 400,
};
const blockTitle: React.CSSProperties = {
  fontSize: '1.05rem',
  margin: '1.5rem 0 0.6rem',
  color: '#212529',
  borderLeft: '4px solid #aa0b56',
  paddingLeft: '0.6rem',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.9rem' };
const journalWarning: React.CSSProperties = {
  background: '#fff8e1',
  border: '1px solid #ffe082',
  color: '#7a4a00',
  borderRadius: 8,
  padding: '0.7rem 0.9rem',
  marginBottom: '1rem',
  fontSize: '0.85rem',
};
const hint: React.CSSProperties = { color: '#868e96', fontSize: '0.78rem', marginTop: 4 };
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #f1f3f5',
};
